import { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner } from "react-bootstrap";
import documentApi from "../../../api/documentapi";

const STATUS_CONFIG = {
  REVIEW: { label: "Under Review", variant: "warning" },
  PARTIALLY_APPROVED: { label: "Partially Approved", variant: "warning" },
  AWAITING_SIGNING: { label: "Awaiting Signing", variant: "info" },
  PARTIALLY_SIGNED: { label: "Partially Signed", variant: "primary" },
  SIGNED: { label: "Signed", variant: "success" },
};

// Small helper for a consistent label/value block with breathing room
const DetailItem = ({ label, value }) => (
  <div className="mb-3">
    <strong>{label}: </strong>
    {value ?? "N/A"}
  </div>
);

const ConsentBrief = ({ documentId, documentName }) => {
  const [consentDetails, setConsentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConsentBrief = async () => {
      try {
        const response = await documentApi.getDocumentDetail(documentId);
        setConsentDetails(response.data);
      } catch (err) {
        setError("Failed to fetch collaboration details.");
        console.error("Error fetching collaboration brief:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchConsentBrief();
  }, [documentId]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-danger mt-4">{error}</div>;
  }

  if (!consentDetails) {
    return <div className="mt-4">No collaboration details found.</div>;
  }

  const status = STATUS_CONFIG[consentDetails.documentStatus] || {
    label: "Unknown",
    variant: "secondary",
  };

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">
          <strong>Details of {documentName}</strong>
        </h4>
        <Button  variant={status.variant} style={{ fontWeight: "bold" }}>
          {status.label}
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-4">
          {/* ================= Sender ================= */}
          <h6 className="text-primary mb-3">
            <strong>Sent By</strong>
          </h6>
          <Row>
            <Col md={6}>
              <DetailItem label="Sender Name" value={consentDetails.senderName} />
            </Col>
            <Col md={6}>
              <DetailItem label="Sender Email" value={consentDetails.senderEmail} />
            </Col>
          </Row>

          <hr className="my-3" />

          {/* ================= Document Details ================= */}
          <h6 className="text-primary mb-3">
            <strong>Document Details</strong>
          </h6>
          <Row>
            <Col md={12}>
              <DetailItem label="Description" value={consentDetails.description || "N/A"} />
            </Col>
            <Col md={6}>
              <DetailItem label="Sent On" value={consentDetails.sentOn} />
            </Col>
            <Col md={6}>
              <DetailItem label="Deadline" value={consentDetails.deadline} />
            </Col>
            <Col md={6}>
              <DetailItem label="Signing Mode" value={consentDetails.signingMode} />
            </Col>
          </Row>

          <hr className="my-3" />

          {/* ================= Reminders ================= */}
          <h6 className="text-primary mb-3">
            <strong>Reminders</strong>
          </h6>
          <Row>
            <Col md={4}>
              <DetailItem
                label="Reminder Every Day"
                value={consentDetails.reminderEveryDay ? "Yes" : "No"}
              />
            </Col>
            <Col md={4}>
              <DetailItem
                label="Reminder Days Before Enabled"
                value={consentDetails.reminderDaysBeforeEnabled ? "Yes" : "No"}
              />
            </Col>
            <Col md={4}>
              <DetailItem
                label="Reminder Last Day"
                value={consentDetails.reminderLastDay ? "Yes" : "No"}
              />
            </Col>
          </Row>

          <hr className="my-3" />

          {/* ================= Charges ================= */}
          <h6 className="text-primary mb-3">
            <strong>Charges</strong>
          </h6>
          <Row>
            <Col md={4}>
              <DetailItem label="Document Charge" value={20} />
            </Col>
            <Col md={4}>
              <DetailItem label="Signatory Charge" value={consentDetails.signatoryCharge} />
            </Col>
            <Col md={4}>
              <DetailItem label="Reviewer Charge" value={consentDetails.reviewerCharge} />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ConsentBrief;